'use client';
import { useEffect, useState, useRef } from 'react';

import { StatisticsProps } from './types';
import { BlurredCard } from '../../blurredCard';

const ANIMATION_TIME = 1000;

export const Statistics = ({
  number,
  units,
  description,
  step = 1,
}: StatisticsProps) => {
  const statisticRef = useRef<HTMLParagraphElement>(null);
  const [isStatisticVisible, setIsStatisticVisible] = useState(false);
  const [currentNumber, setCurrentNumber] = useState(0);
  const interval = Math.floor(ANIMATION_TIME / (number / step));

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      entry.isIntersecting && setIsStatisticVisible(true);
    });

    const statisticBlockRef = statisticRef.current;

    statisticBlockRef && observer.observe(statisticBlockRef);

    return () => {
      statisticBlockRef && observer.unobserve(statisticBlockRef);
    };
  }, []);

  useEffect(() => {
    if (!isStatisticVisible) {
      return;
    }

    const intervalId = setInterval(() => {
      setCurrentNumber((currentNumber) => {
        if (currentNumber >= number) return currentNumber;

        const newNumber = Number.isInteger(currentNumber + step)
          ? currentNumber + step
          : Number((currentNumber + step).toFixed(1));

        currentNumber >= number && clearInterval(intervalId);

        return newNumber;
      });
    }, interval);

    return () => clearInterval(intervalId);
  }, [number, step, interval, isStatisticVisible]);

  return (
    <BlurredCard className="flex flex-col items-start justify-between p-4 md:p-5 md:pt-[60px]">
      <p
        ref={statisticRef}
        className="mb-[30px] whitespace-nowrap md:mb-2 lg:w-[224px]"
      >
        <span className="text-[2.5rem] leading-[4rem] md:text-[4rem]">
          {Number.isInteger(currentNumber)
            ? currentNumber
            : currentNumber.toString().replace('.', ',')}
          +
        </span>
        {units && (
          <span className="text-text-base-secondary ml-2 text-2xl leading-[38px] font-medium md:text-4xl">
            {` ${units}`}
          </span>
        )}
      </p>
      <p className="tracking-tight">{description}</p>
    </BlurredCard>
  );
};
